import type { GridFilterCellProps } from "@tql/ui-components-react/kendo/grid";
import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- match Kendo's `GridFilterCellProps.operator` typing so this component stays assignable to `ComponentType<GridFilterCellProps>`
type FilterOperator = string | Function;

type OperatorTextFilterCellProps = GridFilterCellProps & {
	/**
	 * Custom Kendo filter operator, used instead of the default `"contains"`
	 * (case-insensitive substring match on the raw field value). Use this when
	 * a column's rendered text differs from the value it binds to, so the
	 * column can keep binding to the raw value for correct sorting.
	 */
	operator?: FilterOperator;
};

/**
 * Text filter cell that applies a custom filter operator synchronously, matching
 * the default Kendo filter cell's per-keystroke behavior. Prefer
 * `DebouncedTextFilterCell` only on grids whose row cells are expensive enough
 * that filtering on every keystroke makes typing laggy.
 */
export const OperatorTextFilterCell = ({
	operator = "contains",
	...props
}: OperatorTextFilterCellProps) => {
	const value = (props.value ?? "") as string;

	const handleChange = (event: InputChangeEvent) => {
		const next = String(event.value ?? "");
		props.onChange({
			value: next,
			operator: next === "" ? "" : operator,
			syntheticEvent: event.syntheticEvent,
		});
	};

	return (
		// Kendo only emits these wrapper classes from its own default filter cell,
		// so restore them for the TraxGrid search-icon injector and the grid filter
		// tab navigation hook, which both target `.k-filtercell`.
		<div className={"k-filtercell"}>
			<div className={"k-filtercell-wrapper"}>
				<Input aria-label={"Filter"} onChange={handleChange} value={value} />
			</div>
		</div>
	);
};
